import { isEmpty } from "@forcepoint/platform-utilityui"
import * as singleSpa from "single-spa"
import { changePassFormInit, errorMessages } from "../header.constant"

export const getApplications = (stateApplications) => {
  const appList = stateApplications?.reduce(
    (a, b) => {
      const shouldAddToSwitcher =
        b.showInSwitcher &&
        ((b.isSPA && !isEmpty(b.menus)) ||
          b.applicationType === "external" ||
          b.openInIframe)

      if (shouldAddToSwitcher) {
        switch (b.menuType) {
          case "application":
            a.applications.push(b)
            break
          case "platform":
            a.platforms.push(b)
            break
          case "none":
            a.coreApps.push(b)
            break
          default:
            break
        }
      }

      return a
    },
    { applications: [], platforms: [], coreApps: [] }
  )

  return appList
}

/**
 * To navigate to SPAs
 * @param url
 */
export const navigateToUrl = (url) => {
  singleSpa.navigateToUrl(url)
}

export const getHelpUrl = (stateData) => {
  // Check if Help is enabled in the UI config and return the corresponding URL
  if (stateData?.activeApp?.modules?.uiConfig?.enableHelp) {
    // Try to get the help URL from activeMenu first, then fall back to activeApp
    return (
      stateData.activeMenu?.supportUrls?.help ||
      stateData.activeApp?.supportUrls?.help ||
      null
    )
  }

  // If help is not enabled, return null
  return null
}

export const getUserInitials = (userInfo) => {
  let username = ""
  let userInitials = ""
  /**
   * To store type of tenant-superadmin/mssp/distributor
   */
  const tenantName = JSON.parse(sessionStorage.getItem("tenantName"))

  // Check if tenantName is provided and assign accordingly
  if (tenantName) {
    username = tenantName?.userName ?? "" // Fallback to empty string if userName is missing
    userInitials = tenantName?.userInitials ?? "" // Fallback to empty string if userInitials is missing
  }
  // Otherwise, check if both first_name and last_name are available in userInfo
  else if (userInfo?.first_name && userInfo?.last_name) {
    username = `${userInfo.first_name} ${userInfo.last_name}`
    userInitials = `${userInfo.first_name.charAt(0).toUpperCase()}${userInfo.last_name.charAt(0).toUpperCase()}`
  }
  // If only first_name is available
  else if (userInfo?.first_name) {
    username = userInfo.first_name
    userInitials = userInfo.first_name.slice(0, 2).toUpperCase()
  }
  // If neither first_name nor last_name is available, use username from userInfo
  else {
    username = userInfo?.username ?? "" // Fallback to empty string if username is not available
    userInitials = username.slice(0, 2).toUpperCase() // Safely calculate initials from username
  }

  // Return as an object to ensure consistent result
  return { username, userInitials }
}

export const getMenuList = (applications) => {
  return [
    { menuName: "none", menuList: applications?.coreApps },
    { menuName: "Applications", menuList: applications?.applications },
    { menuName: "Platform Services", menuList: applications?.platforms }
  ]
}

export const validatePasswordStr = (
  passwordStr: string = ""
): { isValid: boolean; errMsg: string } => {
  const obj = {
    isValid: true,
    errMsg: ""
  }

  const errMsg = {
    lc_password_too_short: "Password must be at least 8 characters long.", //NOSONAR
    lc_password_too_long: "Password must not exceed 20 characters.", //NOSONAR
    lc_password_missing_lowercase:
      "Password must contain at least one lowercase letter (a–z).", //NOSONAR
    lc_password_missing_uppercase:
      "Password must contain at least one uppercase letter (A–Z).", //NOSONAR
    lc_password_missing_digit:
      "Password must include at least one digit (0–9).", //NOSONAR
    lc_password_missing_special:
      "Password must include at least one special character: ! @ # $ % ^ & * ( ) _ + - = [ ] { } ; ' : \" \\ | , . < > / ? ", //NOSONAR
    lc_password_disallowed_char: "Password contains invalid character(s): " //NOSONAR
  }
  const specialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/
  const allowedChars = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]$/
  const disallowedChars = []
  for (let char of passwordStr.split("")) {
    if (!allowedChars.test(char) && !disallowedChars.includes(char)) {
      disallowedChars.push(char)
    }
  }

  if (passwordStr.length < 8) {
    obj.isValid = false
    obj.errMsg = errMsg["lc_password_too_short"]
  } else if (passwordStr.length > 20) {
    obj.isValid = false
    obj.errMsg = errMsg["lc_password_too_long"]
  } else if (!/[a-z]/.test(passwordStr)) {
    obj.isValid = false
    obj.errMsg = errMsg["lc_password_missing_lowercase"]
  } else if (!/[A-Z]/.test(passwordStr)) {
    obj.isValid = false
    obj.errMsg = errMsg["lc_password_missing_uppercase"]
  } else if (!/\d/.test(passwordStr)) {
    obj.isValid = false
    obj.errMsg = errMsg["lc_password_missing_digit"]
  } else if (!specialChars.test(passwordStr)) {
    obj.isValid = false
    obj.errMsg = errMsg["lc_password_missing_special"]
  } else if (disallowedChars.length > 0) {
    obj.isValid = false
    const charObj = disallowedChars.join(", ")
    obj.errMsg = `${errMsg["lc_password_disallowed_char"]} ${charObj}`
  }
  return obj
}

/**
 * Helper function to validate password fields.
 * Returns validation status and error messages.
 */
export const validatePasswordFields = (
  formObj: any
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {}
  let isValid = true

  // Validate each password-related field
  for (const field in formObj) {
    if (formObj[field].value === "") {
      isValid = false
    }
    if (formObj[field].touched) {
      const { isValid: isValidPass, errMsg } = validatePasswordStr(
        formObj[field].value
      )
      // Accumulate — plain assignment let a later valid field overwrite an
      // earlier failure, enabling Update with a blank Current Password
      isValid = isValid && isValidPass
      if (errMsg) {
        errors[field] = errMsg
      } else {
        delete errors[field] // Clear error if valid
      }
    }
  }
  // Validate if currentPassword and newPassword are the same
  if (
    formObj.currentPassword.value === formObj.newPassword.value &&
    formObj.currentPassword.value !== ""
  ) {
    isValid = false
    errors["newPassword"] = errorMessages.sameAsCurrentPass.replace(
      "{key}",
      changePassFormInit["newPassword"].text
    )
  }

  // Validate confirmPassword field if filled
  if (
    !errors["confirmPassword"] &&
    formObj.newPassword.value !== formObj.confirmPassword.value &&
    formObj.confirmPassword.value !== ""
  ) {
    isValid = false
    errors["confirmPassword"] = errorMessages.passDoesntMatch
  }

  return { isValid, errors }
}
